import { createEffect, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Lock, Save, Send as SendIcon } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Switch } from "@fleet/ui";
import { mailApi, type MessageDetail, type SendMessageInput } from "../api";
import { useSaveDraft, useSendMessage } from "../hooks";
import { bareAddress } from "./format";
import { Field, MailInput, MailTextarea } from "./field";
import type { ComposeMode } from "../route-search";

interface ComposeFormProps {
  accountId: string;
  mode: ComposeMode;
  original?: MessageDetail;
  onDone: () => void;
}

function splitAddrs(raw: string): string[] {
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildPrefill(mode: ComposeMode, original: MessageDetail | undefined) {
  if (!original || mode === "new") {
    return { to: "", cc: "", subject: "", body: "" };
  }
  const quote = (original.textBody ?? "")
    .split("\n")
    .map((l) => `> ${l}`)
    .join("\n");
  const isForward = mode === "forward";
  // "Re:"/"Fwd:" are conventional RFC-5322 subject prefixes that mail clients
  // parse for threading; translating them would break interop, so they are
  // exempt from i18n.
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- email subject-prefix convention, not UI copy
  const subjectPrefix = isForward ? "Fwd: " : "Re: ";
  const subject = original.subject.startsWith(subjectPrefix)
    ? original.subject
    : `${subjectPrefix}${original.subject}`;
  const to = isForward ? "" : bareAddress(original.from);
  const cc =
    mode === "reply-all"
      ? original.to
          .map(bareAddress)
          .filter((a) => a !== bareAddress(original.from))
          .join(", ")
      : "";
  const body = `\n\n---\n${quote}`;
  return { to, cc, subject, body };
}

export function ComposeForm(props: ComposeFormProps) {
  const { t } = useI18n();
  const sendMutation = useSendMessage();
  const draftMutation = useSaveDraft();

  const [to, setTo] = createSignal("");
  const [cc, setCc] = createSignal("");
  const [bcc, setBcc] = createSignal("");
  const [subject, setSubject] = createSignal("");
  const [body, setBody] = createSignal("");
  const [encrypt, setEncrypt] = createSignal(false);
  const [status, setStatus] = createSignal<string | null>(null);

  // Prefill once the original (reply/forward source) is available.
  createEffect(() => {
    const pre = buildPrefill(props.mode, props.original);
    setTo(pre.to);
    setCc(pre.cc);
    setSubject(pre.subject);
    setBody(pre.body);
  });

  const buildBody = async (): Promise<SendMessageInput> => {
    const base: SendMessageInput = {
      to: splitAddrs(to()),
      cc: splitAddrs(cc()),
      bcc: splitAddrs(bcc()),
      subject: subject(),
      text: body(),
    };
    if (props.original && props.mode !== "forward" && props.mode !== "new") {
      base.inReplyTo = props.original.messageId;
      base.references = [...props.original.references, props.original.messageId];
    }
    if (encrypt()) {
      const firstTo = splitAddrs(to())[0];
      if (firstTo) {
        try {
          const key = await mailApi.lookupE2eKey(firstTo);
          if (key.publicKey) {
            base.e2ePublicKey = key.publicKey;
          }
        } catch {
          // No published key for the recipient — fall through to plaintext send.
        }
      }
    }
    return base;
  };

  const handleSend = async () => {
    setStatus(null);
    if (splitAddrs(to()).length === 0) {
      setStatus(t(msg`Add at least one recipient.`));
      return;
    }
    const payload = await buildBody();
    if (encrypt() && !payload.e2ePublicKey) {
      setStatus(t(msg`No encryption key found for the recipient — send unencrypted or try later.`));
      return;
    }
    sendMutation.mutate(
      { accountId: props.accountId, body: payload },
      {
        onSuccess: () => props.onDone(),
        onError: (err) => setStatus(err.message),
      },
    );
  };

  const handleDraft = async () => {
    setStatus(null);
    const payload = await buildBody();
    draftMutation.mutate(
      { accountId: props.accountId, body: payload },
      {
        onSuccess: () => setStatus(t(msg`Draft saved.`)),
        onError: (err) => setStatus(err.message),
      },
    );
  };

  return (
    <form
      class="flex flex-col gap-3"
      data-testid="mail-compose-form"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSend();
      }}
    >
      <Field label={t(msg`To`)}>
        <MailInput
          value={to()}
          onInput={(e) => setTo(e.currentTarget.value)}
          placeholder="alice@example.com, bob@example.com"
          data-testid="compose-to"
          autocomplete="off"
        />
      </Field>
      <div class="grid gap-3 sm:grid-cols-2">
        <Field label={t(msg`Cc`)}>
          <MailInput
            value={cc()}
            onInput={(e) => setCc(e.currentTarget.value)}
            data-testid="compose-cc"
            autocomplete="off"
          />
        </Field>
        <Field label={t(msg`Bcc`)}>
          <MailInput
            value={bcc()}
            onInput={(e) => setBcc(e.currentTarget.value)}
            data-testid="compose-bcc"
            autocomplete="off"
          />
        </Field>
      </div>
      <Field label={t(msg`Subject`)}>
        <MailInput
          value={subject()}
          onInput={(e) => setSubject(e.currentTarget.value)}
          data-testid="compose-subject"
        />
      </Field>
      <Field label={t(msg`Message`)}>
        <MailTextarea
          value={body()}
          onInput={(e) => setBody(e.currentTarget.value)}
          class="min-h-56"
          data-testid="compose-body"
        />
      </Field>

      <label class="flex items-center gap-2.5 rounded-md border border-border/40 bg-card/30 px-3 py-2.5">
        <Lock class="h-4 w-4 text-emerald-500" />
        <span class="flex-1 text-sm">
          <span class="font-medium">{t(msg`Encrypt (E2E)`)}</span>
          <span class="block text-xs text-muted-foreground/80">
            {t(msg`Seal the body for the recipient if they have a Cartero key.`)}
          </span>
        </span>
        <Switch
          checked={encrypt()}
          onCheckedChange={setEncrypt}
          aria-label={t(msg`Encrypt (E2E)`)}
        />
      </label>

      <Show when={status()}>
        <p class="text-sm text-muted-foreground" data-testid="compose-status" role="status">
          {status()}
        </p>
      </Show>

      <div class="flex items-center gap-2">
        <Button
          type="submit"
          class="gap-1.5"
          disabled={sendMutation.isPending}
          data-testid="compose-send"
        >
          <SendIcon class="h-4 w-4" />
          {sendMutation.isPending ? t(msg`Sending…`) : t(msg`Send`)}
        </Button>
        <Button
          type="button"
          variant="outline"
          class="gap-1.5"
          disabled={draftMutation.isPending}
          onClick={() => void handleDraft()}
          data-testid="compose-draft"
        >
          <Save class="h-4 w-4" />
          {t(msg`Save draft`)}
        </Button>
        <Button type="button" variant="ghost" onClick={() => props.onDone()}>
          {t(msg`Cancel`)}
        </Button>
      </div>
    </form>
  );
}
